<script setup>
import { computed, h, ref, shallowRef } from 'vue';
import { madridNow, dateInputFormat, toMadridISO, validateAbsenceRange } from '@/utils/presenceAbsence';
// eslint-disable-next-line no-undef
const props = defineProps({
  pauseReason: { type: String, required: true },
  initialObservations: { type: String, default: '' },
  busy: Boolean,
  error: { type: String, default: '' },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['confirm', 'cancel']);
const calendarIcon = shallowRef({ render: () => h('img', { src: '/calendar-blue.svg', class: 'presence-calendar-icon', alt: '' }) });
const fromDate = ref(madridNow());
const toDate = ref('');
const observations = ref(props.initialObservations);
const validationError = ref('');
const typeName = computed(() => props.pauseReason === 'other' ? 'Otros' : 'Enfermedad o accidente de origen no laboral');
const defaultPickerDate = ref(madridNow());
// Keep callbacks stable: Element Plus reads them when mounting its time panel.
function createTimeLimits(getSelectedDate, getBoundary, before) {
  function disabledMinutes(hour) {
    const selected = getSelectedDate() || defaultPickerDate.value;
    const boundary = getBoundary();
    if (!boundary || selected.slice(0, 10) !== boundary.slice(0, 10)) return [];
    const limit = Number(boundary.slice(11, 13)) * 60 + Number(boundary.slice(14, 16));
    return Array.from({ length: 60 }, (_, minute) => minute)
      .filter(minute => before ? hour * 60 + minute >= limit : hour * 60 + minute <= limit);
  }
  return {
    disabledHours: () => Array.from({ length: 24 }, (_, hour) => hour)
      .filter(hour => disabledMinutes(hour).length === 60),
    disabledMinutes,
  };
}
const fromTimeLimits = createTimeLimits(() => fromDate.value, () => toDate.value, true);
const toTimeLimits = createTimeLimits(() => toDate.value, () => fromDate.value, false);
// Calendar limits use local calendar days, just like the date picker.
function disableFromDate(date) {
  return !!toDate.value && date > new Date(toDate.value.slice(0, 10) + "T00:00:00");
}
function disableToDate(date) {
  return !!fromDate.value && date < new Date(fromDate.value.slice(0, 10) + "T00:00:00");
}
function updateDate(field, value) {
  const from = field === "from" ? value : fromDate.value;
  const to = field === "to" ? value : toDate.value;
  const error = from && to ? validateAbsenceRange(from, to) : "";
  if (error) {
    validationError.value = error;
    return;
  }
  if (field === "from") fromDate.value = value;
  else toDate.value = value;
  validationError.value = "";
}
function submit() {
  if (props.busy) return;
  const errors = [];
  const dateError = validateAbsenceRange(fromDate.value, toDate.value);
  if (dateError) errors.push(dateError);
  if (!observations.value.trim()) {
    errors.push("Complete las observaciones.");
  }
  validationError.value = errors.join(" ");
  if (validationError.value) return;
  emit('confirm', {
    fromDate: toMadridISO(fromDate.value),
    toDate: toMadridISO(toDate.value),
    observations: observations.value.trim(),
  });
}
</script>

<template>
  <form class="presence-absence-form" @submit.prevent="submit">
    <div class="row absence-dates">
      <div class="col-12 col-md-6 absence-date-column">
        <div class="form-item date-picker">
          <label for="pause-absence-from" class="w-100">*Desde:</label>
          <el-date-picker id="pause-absence-from" :model-value="fromDate" :disabled-date="disableFromDate" v-bind="fromTimeLimits"
            @update:model-value="$event => updateDate('from', $event)" type="datetime" class="w-100"
            placeholder="Desde" :prefix-icon="calendarIcon" format="DD/MM/YYYY HH:mm"
            :default-value="new Date(defaultPickerDate.replace(' ', 'T'))"
            :value-format="dateInputFormat" :disabled="busy" :clearable="false" />
        </div>
      </div>
      <div class="col-12 col-md-6 absence-date-column">
        <div class="form-item date-picker">
          <label for="pause-absence-to" class="w-100">*Hasta:</label>
          <el-date-picker id="pause-absence-to" :model-value="toDate" :disabled-date="disableToDate" v-bind="toTimeLimits"
            @update:model-value="$event => updateDate('to', $event)" type="datetime" class="w-100"
            placeholder="Hasta" :prefix-icon="calendarIcon" format="DD/MM/YYYY HH:mm"
            :default-value="new Date(defaultPickerDate.replace(' ', 'T'))"
            :value-format="dateInputFormat" :disabled="busy" :clearable="false" />
        </div>
      </div>
    </div>
    <div class="form-item absence-type">
      <label for="pause-absence-type" class="w-100">*Tipo de absentismo</label>
      <el-input id="pause-absence-type" :model-value="typeName" readonly class="w-100" />
    </div>
    <div class="form-item absence-notes">
      <label for="pause-absence-notes" class="w-100">*Observaciones</label>
      <textarea id="pause-absence-notes" v-model="observations" class="font18 w-100 p-2"
        placeholder="Observaciones del motivo de la baja" :disabled="busy" />
    </div>
    <p v-if="validationError || error" class="text-danger mt-3" role="alert">{{ validationError || error }}</p>
    <div class="form-actions d-flex justify-content-end gap-3">
      <button type="button" class="btn btn_red px-4" :disabled="busy" @click="emit('cancel')">Cancelar</button>
      <button type="submit" class="btn btn_yellow px-4" :disabled="busy">Guardar</button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.presence-absence-form {
  margin-top: 8px;
  margin-right: 5%;
  .form-item { margin-bottom: 0; }
  .absence-dates { display: flex; flex-wrap: wrap; margin-bottom: 18px; }
  .absence-date-column { min-width: 0; }
  .absence-type { margin-bottom: 38px; }
  :deep(.el-date-editor.el-input) { width: 100%; min-width: 0; }
  :deep(.presence-calendar-icon) { width: 16px; height: 16px; margin-right: 10px; }
  .absence-notes textarea {
    display: block;
    height: 200px;
    border: 1px solid #000;
    border-radius: 20px;
    box-shadow: none;
    outline: none;
    resize: vertical;
  }
  .absence-notes textarea:hover,
  .absence-notes textarea:focus {
    border-color: #409eff;
    box-shadow: 0 0 0 1px #409eff inset;
  }
  .form-actions {
    margin-top: 38px;
    padding-right: 14px;
  }
  .form-actions button {
    flex: 0 0 25%;
    width: 25%;
    min-height: 34px;
    margin: 0;
  }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
}
@media (min-width: 768px) {
  .presence-absence-form .absence-date-column { flex: 0 0 50%; width: 50%; }
  .presence-absence-form .absence-date-column .date-picker { width: 90%; }
}
@media (max-width: 767px) {
  .presence-absence-form {
    margin-right: 0;
    .absence-dates { row-gap: 18px; }
    .absence-date-column { flex: 0 0 100%; width: 100%; }
    .absence-type { margin-bottom: 28px; }
    .form-actions { padding-right: 0; flex-direction: column; margin-top: 24px; }
    .form-actions button { flex: none; width: 100%; min-height: 44px; }
  }
}
</style>
